/**
 * The last minute of what someone did, kept in memory.
 *
 * A report's "steps to reproduce" box reliably returns "it didn't work". What actually
 * reproduces a bug is the trail: which screens they moved through, what they clicked, which
 * calls failed and what the console said. This records that as it happens so the widget has
 * something to send that nobody had to remember.
 *
 * Nothing here is persisted or sent anywhere on its own — the buffer is read once, at the
 * moment a report is raised, and dies with the tab otherwise.
 */

export interface Breadcrumb {
  at: string;
  kind: "route" | "click" | "api" | "console";
  label: string;
  detail?: string;
}

const LIMIT = 40;
const trail: Breadcrumb[] = [];

/** Long strings get cut here rather than at the server, which would reject the whole report. */
function clip(s: string, n: number): string {
  const flat = s.replace(/\s+/g, " ").trim();
  return flat.length > n ? `${flat.slice(0, n - 1)}…` : flat;
}

export function record(kind: Breadcrumb["kind"], label: string, detail?: string): void {
  trail.push({
    at: new Date().toISOString(),
    kind,
    label: clip(label, 280),
    ...(detail ? { detail: clip(detail, 560) } : {}),
  });
  if (trail.length > LIMIT) trail.splice(0, trail.length - LIMIT);
}

export function readTrail(): Breadcrumb[] {
  return [...trail];
}

/**
 * What a click landed on, described the way a person would say it.
 *
 * The accessible name first, then the visible text, then the selector — so a report reads
 * "clicked Approve the unit" rather than a chain of div indices.
 */
function describe(el: Element): string {
  const actionable = el.closest("button, a, [role='tab'], [role='button'], input, select") ?? el;
  const label =
    actionable.getAttribute("aria-label") ??
    (actionable as HTMLElement).innerText?.slice(0, 80) ??
    actionable.getAttribute("title") ??
    "";
  const flat = label.replace(/\s+/g, " ").trim();
  const tag = actionable.tagName.toLowerCase();
  const cls = (actionable.className || "").toString().split(/\s+/).filter(Boolean)[0];
  return flat || `<${tag}${cls ? `.${cls}` : ""}>`;
}

/**
 * Start recording. Called once, from the shell.
 *
 * Console capture wraps rather than replaces, so anything already listening still sees it
 * and the browser's own console still shows the original call site.
 */
export function startRecording(): void {
  if ((window as unknown as { __schoolverseTrail?: boolean }).__schoolverseTrail) return;
  (window as unknown as { __schoolverseTrail?: boolean }).__schoolverseTrail = true;

  document.addEventListener(
    "click",
    (e) => {
      const el = e.target as Element | null;
      if (!el || !el.closest) return;
      /* Not the widget's own controls — a report should not be a record of raising it. */
      if (el.closest("[data-report-widget]")) return;
      record("click", describe(el));
    },
    /* Capture phase: a handler that stops propagation should not hide the click. */
    true,
  );

  for (const level of ["error", "warn"] as const) {
    const original = console[level];
    console[level] = (...args: unknown[]) => {
      try {
        record("console", level, args.map((a) => (a instanceof Error ? a.message : String(a))).join(" "));
      } catch {
        /* Recording a message must never be the reason a screen breaks. */
      }
      original.apply(console, args as []);
    };
  }

  window.addEventListener("error", (e) => record("console", "uncaught", e.message));
  window.addEventListener("unhandledrejection", (e) =>
    record("console", "unhandled rejection", String((e as PromiseRejectionEvent).reason)),
  );
}
